import React, { useEffect, useRef, useState } from 'react';
import { RootState } from 'src/store/reducers/state';
import { useParams } from 'react-router-dom';
import { useEventActions } from 'src/store/event/actions';
import { useDispatch, useSelector } from 'react-redux';
import { EventRegistrationImagesViewModel, PatchEmailSettingsData } from 'src/models/eventModel';
import { useForm } from 'react-hook-form';
import Editor, { EditorHandle } from 'src/components/Editor/MergeFieldEditor';
import DemoEmailTemplateModal from './DemoEmailTemplateModal';
import { useVideosActions } from 'src/store/videos/actions';
import { ImageStringConverter } from 'src/components/utilities/utils';
import { EventReminders } from './EmailReminders';
import { MergeField } from 'src/models/eventModel';
const mergeFields: MergeField[] = [
    { token: '%FirstName%', description: 'First name' },
    { token: '%LastName%', description: 'Last name' },
    { token: '%Username%', description: 'Username' },
    { token: '%Email%', description: 'Email address' },
    { token: '%Title%', description: 'Event title' },
    { token: '%Date%', description: 'Event date' },
    { token: '%Time%', description: 'Event time' },
    { token: '%Duration%', description: 'Event duration' },
    { token: '%HostUrl%', description: 'Event page link' },
];

const Toggle: React.FC<{ checked: boolean; onChange: () => void }> = ({ checked, onChange }) => (
    <button
        type="button"
        onClick={onChange}
        className={`relative w-10 h-5 rounded-full flex-shrink-0 transition-colors ${checked ? 'bg-indigo-600' : 'bg-gray-300'
            }`}>
        <span
            className="absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all"
            style={{ left: checked ? '20px' : '2px' }}
        />
    </button>
);

const InfoIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-400">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="16" x2="12" y2="12" />
        <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
);

const EmailNotifications = ({ editEventClicked, setCurrentStep }: any) => {
    const dispatch = useDispatch();
    const eventActions = useEventActions(dispatch);
    const videoActions = useVideosActions(dispatch);
    const { eventId } = useParams<string>();
    const [activeTab, setActiveTab] = useState<'confirmation' | 'reminders'>('confirmation');
    const [editorState, setEditorState] = useState<string>('');
    const [googleCalendar, setGoogleCalendar] = useState<boolean>(false);
    const { register, handleSubmit, reset, setValue, watch } = useForm<EventRegistrationImagesViewModel>();
    const editorRef = useRef<EditorHandle>(null);

    const { eventRegistrationImagesView, isEventSaved, isEventUpdated, mediaUrl, isGetSingleEventLoading } = useSelector(
        (state: RootState) => ({
            eventRegistrationImagesView: state.event.singleEvent.eventRegistrationField
                ? state.event.singleEvent.eventRegistrationField.eventRegistrationImagesViewModel
                : null,
            isEventSaved: state.event.isEventSaved,
            isEventUpdated: state.event.isEventUpdated,
            mediaUrl: state.videos.mediaUrl,
            isGetSingleEventLoading: state.event.isGetSingleEventLoading
        })
    );

    const insertField = (token: string) => {
        if (editorRef.current) {
            editorRef.current.insertAtCursor(token);
        } else {
            setEditorState((prev) => `${prev}${prev.endsWith('\n') || prev.length === 0 ? '' : ' '}${token}`);
        }
    };

    useEffect(() => {
        if (eventRegistrationImagesView && Object.keys(eventRegistrationImagesView).length && editEventClicked) {
            videoActions.getMediaUrlRequest(eventRegistrationImagesView.emailBody);
        }
    }, [eventRegistrationImagesView, editEventClicked]);
    useEffect(() => {
        if (eventId) {
            eventActions.getSingleEventRequest(eventId)
        }
    }, [eventId])
    useEffect(() => {
        if (mediaUrl) {
            setEditorState(mediaUrl);
        }
    }, [mediaUrl]);

    useEffect(() => {
        if (isEventSaved === true) {
            reset({
                emailOnRegistration: true,
                emailSubject: '',
                emailBody: '',
                sendICSAttachment: true,
            });
        }
    }, [isEventSaved]);
    useEffect(() => {
        if (eventId) {
            eventActions.getSingleEventRequest(eventId)
        }
    }, [])
    useEffect(() => {
        if (eventRegistrationImagesView) {
            setEditorState(eventRegistrationImagesView.emailBody);
            reset(eventRegistrationImagesView);
        }
        if (eventId?.length === 0) {
            reset({
                emailOnRegistration: true,
                emailSubject: '',
                emailBody: '',
                sendICSAttachment: true,
            });
        }
    }, [eventRegistrationImagesView]);

    const onSubmit = (data: any) => {
        const patchJsonModel: PatchEmailSettingsData = {
            id: data.id,
            eventId: data.eventId,
            emailOnRegistration: data.emailOnRegistration,
            emailSubject: data.emailSubject,
            sendICSAttachment: data.sendICSAttachment,
            emailBody: ImageStringConverter(editorState),
        };
        if (patchJsonModel) eventActions.updateEmailSettingRequest(patchJsonModel);
        setCurrentStep(7)
    };

    return (
        <>
            <main className="flex-1 flex flex-col min-w-0">
                <div className="bg-white rounded-xl border border-gray-200 flex-1 flex flex-col">
                    {/* Header */}
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 px-4 sm:px-8 pt-5 sm:pt-6 pb-4 border-b border-gray-100">
                        <div className="min-w-0">
                            <h2 className="text-lg font-semibold text-gray-900 m-0">Email & Notifications</h2>
                            <p className="text-sm text-gray-400 mt-1">
                                Set up your registration confirmation email and automated reminder emails.
                            </p>
                        </div>
                        <button
                            type="button"
                            className="flex-shrink-0 self-start flex items-center gap-1.5 text-sm font-semibold text-indigo-600 border border-indigo-200 rounded-lg px-3 py-1.5 hover:bg-indigo-50"
                            data-bs-toggle="modal"
                            data-bs-target="#DemoEmailTemplateModal">
                            Launch Demo Templates
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                                <polyline points="15 3 21 3 21 9" />
                                <line x1="10" y1="14" x2="21" y2="3" />
                            </svg>
                        </button>
                    </div>

                    <div className="flex items-center gap-6 px-8 pt-3 border-b border-gray-100">
                        <button
                            type="button"
                            onClick={() => setActiveTab('confirmation')}
                            className={`text-sm font-semibold pb-3 border-b-2 -mb-px ${activeTab === 'confirmation' ? 'text-indigo-600 border-indigo-600' : 'text-gray-400 border-transparent'
                                }`}>
                            Registration Confirmation
                        </button>
                        <button
                            type="button"
                            onClick={() => setActiveTab('reminders')}
                            className={`text-sm font-semibold pb-3 border-b-2 -mb-px ${activeTab === 'reminders' ? 'text-indigo-600 border-indigo-600' : 'text-gray-400 border-transparent'
                                }`}>
                            Event Reminders
                        </button>
                    </div>

                    {activeTab === 'confirmation' ? (
                        <div className="px-4 sm:px-8 py-5 sm:py-6">
                            <div className="flex flex-col xl:flex-row gap-6">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-start gap-3 min-w-0">
                                        <Toggle
                                            checked={watch('emailOnRegistration')}
                                            onChange={() => setValue('emailOnRegistration', !watch('emailOnRegistration'))}
                                        />
                                        <div>
                                            <p className="m-0 text-sm font-semibold text-gray-700">Send a registration confirmation email</p>
                                            <p className="m-0 text-xs text-gray-400 mt-0.5">
                                                Automatically send an email to users after they register for this event.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="mt-5">
                                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                            Email Subject <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400"
                                            {...register('emailSubject')}
                                        />
                                    </div>


                                    <div className="mt-5">
                                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                            Email Body <span className="text-red-500">*</span>
                                        </label>
                                        <div className="border border-gray-200 rounded-lg overflow-x-auto max-w-full">
                                            <Editor ref={editorRef} editorState={editorState} setEditorState={setEditorState} />
                                        </div>
                                        <div className="text-right text-xs text-gray-400 px-3 pb-2">
                                            Characters: {editorState.length}
                                        </div>
                                    </div>

                                    <div className="border-t border-gray-100 mt-6 pt-6">
                                        <p className="flex items-center gap-1.5 text-sm font-semibold text-gray-800 m-0">
                                            Email Attachments <span className="text-gray-400 font-normal">(Optional)</span>
                                            <InfoIcon />
                                        </p>
                                        <div className="mt-4 space-y-4">
                                            <div className="flex items-start gap-3 min-w-0">
                                                <Toggle
                                                    checked={watch('sendICSAttachment')}
                                                    onChange={() => setValue('sendICSAttachment', !watch('sendICSAttachment'))}
                                                />
                                                <div>
                                                    <p className="m-0 text-sm font-semibold text-gray-700">Send ICS calendar attachment</p>
                                                    <p className="m-0 text-xs text-gray-400 mt-0.5">
                                                        Include an .ics file so attendees can add the event to their calendar.
                                                    </p>
                                                </div>
                                            </div>
                                            {/* <div className="flex items-start gap-3">
                                                <Toggle checked={googleCalendar} onChange={() => setGoogleCalendar(!googleCalendar)} />
                                                <span className="flex items-center justify-center w-6 h-6 rounded bg-white border border-gray-200 flex-shrink-0">
                                                    <svg width="14" height="14" viewBox="0 0 24 24">
                                                        <rect x="3" y="3" width="18" height="18" rx="2" fill="#4285F4" />
                                                        <text x="12" y="16" fontSize="10" textAnchor="middle" fill="#fff" fontFamily="Arial">31</text>
                                                    </svg>
                                                </span>
                                                <div>
                                                    <p className="m-0 text-sm font-semibold text-gray-700">Enable Google Calendar Integration</p>
                                                    <p className="m-0 text-xs text-gray-400 mt-0.5">
                                                        Allow users to add this event to Google Calendar from the confirmation email.
                                                    </p>
                                                </div>
                                            </div> */}
                                        </div>
                                    </div>
                                </div>

                                <div className="w-full xl:w-80 flex-shrink-0 bg-indigo-50 rounded-lg p-4 h-fit min-w-0">
                                    <p className="flex items-center gap-1.5 text-sm font-semibold text-gray-900 m-0">
                                        Available Merge Fields <InfoIcon />
                                    </p>
                                    <p className="text-xs text-gray-500 mt-1 mb-3">Click a field to insert it into your email.</p>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-1 gap-1">
                                        {mergeFields.map((field) => (
                                            <button
                                                key={field.token}
                                                type="button"
                                                onClick={() => insertField(field.token)}
                                                className="w-full flex items-center gap-2 bg-white rounded-md px-3 py-2 text-left hover:bg-gray-50">
                                                <span className="text-xs font-semibold text-indigo-600 w-24 sm:w-28 flex-shrink-0 truncate">
                                                    {field.token}
                                                </span>
                                                <span className="text-xs text-gray-400 flex-1 truncate">{field.description}</span>
                                                <svg
                                                    width="13"
                                                    height="13"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    className="text-gray-300 flex-shrink-0">
                                                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                                                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                                                </svg>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <EventReminders />
                    )}


                    <div className="flex justify-between gap-3 px-4 sm:px-8 py-4 sm:py-5 border-t border-gray-100">
                        <button
                            type="button"
                            onClick={() => setCurrentStep(5)}
                            className="flex-shrink-0 flex items-center gap-2 text-sm font-semibold text-gray-600 border border-gray-200 rounded-lg px-4 sm:px-5 py-2.5 hover:bg-gray-50">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <line x1="19" y1="12" x2="5" y2="12" />
                                <polyline points="12 19 5 12 12 5" />
                            </svg>
                            Back
                        </button>
                        <button
                            onClick={handleSubmit(onSubmit)}
                            type="button"
                            disabled={isGetSingleEventLoading}
                            className="flex-1 sm:flex-none justify-center flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-70 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg px-4 sm:px-5 py-2.5"
                        >
                            {isGetSingleEventLoading ? (
                                <>
                                    <svg
                                        className="animate-spin h-4 w-4"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        />
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                        />
                                    </svg>
                                    Loading...
                                </>
                            ) : (
                                <>
                                    Next: Review & Publish
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2.5"
                                    >
                                        <line x1="5" y1="12" x2="19" y2="12" />
                                        <polyline points="12 5 19 12 12 19" />
                                    </svg>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </main>
            <DemoEmailTemplateModal />
        </>
    );
};

export default EmailNotifications;